import { useState, useEffect, useMemo, useCallback } from "react";
import { usePermission } from "@/hooks/usePermission";
import { showSuccess, showError } from "@/lib/utils/notifications";
import { gatewayMetaLookup, getGatewayStatuses } from "@/lib/api/paymentLogService";
import type { GatewayStatusMetaMap } from "@/types/paymentLog";
import {
  clearSignature,
  openChargebackPacketDownload,
  saveNameOverride,
  submitPaymentFailedUnfreeze,
  submitPaymentRefundAdjustment,
  submitDefaultInvoice,
} from "../services/applicationService";
import { deletePaymentLogOrAdjustment } from "@/lib/api/paymentService";
import { parseDigitalSignature } from "../utils/signaturePaymentHelpers";
import { buildDashboardReportPath } from "@/features/reports/utils/reportRoutePaths";
import {
  getPaymentLogDetailRoute,
  getChargebackDetailReactPath,
  isChargebackPaymentType,
} from "../utils/signaturePaymentLogActions";
import type { SignaturePaymentTabProps, PaymentLine } from "../types/tabs";
import {
  computeBalanceOwedAmount,
  computeIsApplicationPaidForBalanceDue,
  filterPaymentLogRowsForApplicant,
  isPaymentNameOverrideSectionVisible,
  normalizePaymentNameOverrideChoice,
  shouldShowAccessFeeBalanceDueSection,
  shouldShowBalanceOwedSection,
  parsePaymentFailedList,
} from "../components/tabs/signaturePaymentTabLogic";

export function useSignaturePaymentTab({
  applicationAllData,
  applicationData,
  applicantId,
  onRefresh,
}: SignaturePaymentTabProps) {
  const { hasPolicy } = usePermission();

  const canClearSignature = hasPolicy(["signature_removal"]);
  const canPerformPaymentActions = hasPolicy(["application_detail_payment_log_action"]);
  const canEditPayment = hasPolicy(["application_detail_payment_log_edit"]);
  const canDeletePayment = hasPolicy(["application_detail_payment_log_delete"]);
  const canEditApplication = hasPolicy(["application_edit"]);

  const paymentFailedList = parsePaymentFailedList(applicationData?.payment_failed);
  const failedApiLogs = applicationData?.failed_api_logs ?? [];

  const [showAgreementsModal, setShowAgreementsModal] = useState(false);
  const [clearingSignature, setClearingSignature] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState<string | number | null>(null);
  const [showExternalPayment, setShowExternalPayment] = useState(false);
  const [gatewayMeta, setGatewayMeta] = useState<GatewayStatusMetaMap | null>(null);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedPaymentLine, setSelectedPaymentLine] = useState<PaymentLine | null>(null);
  const [nameOverrideChoice, setNameOverrideChoice] = useState<"YES" | "NO">("NO");
  const [nameOverrideReasonSelect, setNameOverrideReasonSelect] = useState("");
  const [isSubmittingOverride, setIsSubmittingOverride] = useState(false);
  const [unfreezeReason, setUnfreezeReason] = useState("");
  const [submittingUnfreeze, setSubmittingUnfreeze] = useState(false);
  const [clearSignatureConfirmOpen, setClearSignatureConfirmOpen] = useState(false);
  const [paymentDeleteConfirm, setPaymentDeleteConfirm] = useState<{
    open: boolean;
    payment: PaymentLine | null;
  }>({ open: false, payment: null });

  const externalPaymentType =
    applicationData?.applicant_status === "INCOMPLETE" ? "APPLICATION_FEE" : "CONTRACT";

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const meta = await getGatewayStatuses();
        if (!cancelled) setGatewayMeta(meta);
      } catch {
        /* Rows still show raw gateway_status when lookup is unavailable */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const isPendingDefault = sessionStorage.getItem("pending_default_payment") === "1";
    if (isPendingDefault && applicationAllData?.payment_and_payment_adjustment) {
      sessionStorage.removeItem("pending_default_payment");

      let hasDefault = false;
      let hasDefaultInvoice = false;
      let paymentDetailInfo: PaymentLine | null = null;

      const adjustments = applicationAllData.payment_and_payment_adjustment as PaymentLine[];
      adjustments.forEach((paymentDetail) => {
        if (paymentDetail.payment_type === "DEFAULT") {
          hasDefault = true;
          paymentDetailInfo = paymentDetail;
        }
        if (paymentDetail.payment_type === "DEFAULT_INVOICE") {
          hasDefaultInvoice = true;
        }
      });

      if (hasDefault && !hasDefaultInvoice && paymentDetailInfo) {
        setSelectedPaymentLine(paymentDetailInfo);
        setShowAdjustmentModal(true);
      }
    }
  }, [applicationAllData]);

  useEffect(() => {
    if (!isPaymentNameOverrideSectionVisible(applicationData)) return;
    setNameOverrideChoice(
      normalizePaymentNameOverrideChoice(applicationData?.payment_name_override),
    );
    const r = applicationData?.payment_name_override_reason;
    setNameOverrideReasonSelect(typeof r === "string" ? r : r != null ? String(r) : "");
  }, [
    applicationData?.payment_name_override,
    applicationData?.payment_name_override_reason,
  ]);

  const refreshAfterAdminAction = async () => {
    if (onRefresh) {
      await onRefresh();
    } else {
      window.location.reload();
    }
  };

  const isApplicationPaid = computeIsApplicationPaidForBalanceDue(
    applicationAllData,
    applicationData,
    applicantId,
  );

  const showAccessFeeBalanceDue = shouldShowAccessFeeBalanceDueSection(
    applicationAllData,
    applicationData,
    isApplicationPaid,
  );

  const showBalanceOwed = shouldShowBalanceOwedSection(
    applicationAllData,
    applicationData,
    isApplicationPaid,
  );

  const balanceOwedAmount = computeBalanceOwedAmount();

  const parsedSignature = parseDigitalSignature(
    applicationData?.digital_signature,
    applicationData?.signature,
  );

  const paymentLogRowsForCurrentApplicant = useMemo(
    () => filterPaymentLogRowsForApplicant(applicationAllData, applicantId),
    [applicationAllData, applicantId],
  );

  const performClearSignature = async () => {
    setClearSignatureConfirmOpen(false);
    setClearingSignature(true);
    try {
      await clearSignature(applicantId);
      showSuccess("Signature Info Removed Successfully");
      await refreshAfterAdminAction();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to clear signature");
    } finally {
      setClearingSignature(false);
    }
  };

  const performDeletePayment = async () => {
    const payment = paymentDeleteConfirm.payment;
    setPaymentDeleteConfirm({ open: false, payment: null });
    if (!payment) return;
    const deletingKey = payment.payment_log_id ?? payment.pa_id ?? null;
    if (deletingKey == null) return;
    setDeletingPaymentId(deletingKey);
    try {
      await deletePaymentLogOrAdjustment(payment);
      showSuccess("Payment adjustment deleted successfully");
      await refreshAfterAdminAction();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to delete payment log");
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const handleViewPaymentLog = useCallback(async (payment: PaymentLine) => {
    const route = getPaymentLogDetailRoute(payment);
    if (!route) return;

    const url = `${window.location.origin}${buildDashboardReportPath(route)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const handleEditPaymentLog = useCallback(
    async (payment: PaymentLine) => {
      if (isChargebackPaymentType(payment)) {
        const paId = payment.pa_id;
        if (paId == null || paId === "") return;

        const url = `${window.location.origin}${getChargebackDetailReactPath(applicantId, paId)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      setSelectedPaymentLine(payment);
      setShowAdjustmentModal(true);
    },
    [applicantId],
  );

  const handleDownloadChargebackPacket = () => {
    try {
      openChargebackPacketDownload(applicantId);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to download packet");
    }
  };

  const handleSaveNameOverride = async () => {
    setIsSubmittingOverride(true);
    try {
      await saveNameOverride(applicantId, nameOverrideChoice, nameOverrideReasonSelect);
      showSuccess("Setting Save Successfully");
      await refreshAfterAdminAction();
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : "Failed to save override");
    } finally {
      setIsSubmittingOverride(false);
    }
  };

  const handleUnfreezePayment = async () => {
    setSubmittingUnfreeze(true);
    try {
      await submitPaymentFailedUnfreeze(applicantId, {
        unfreeze_reason: unfreezeReason.trim(),
      });
      showSuccess("Setting Save Successfully");
      await refreshAfterAdminAction();
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : "Failed to unfreeze payment");
    } finally {
      setSubmittingUnfreeze(false);
    }
  };

  const handlePaymentAdjustmentSubmit = async (data: Record<string, unknown>) => {
    if (!selectedPaymentLine?.payment_log_id) {
      showError("Cannot adjust payment without a payment log id.");
      return;
    }
    // Angular: a new Default Invoice posts to /api/default_invoice/:applicant (no pa_id yet).
    if (data.adjustment_type === "default_invoice" && !selectedPaymentLine.pa_id) {
      await submitDefaultInvoice(applicantId, data);
    } else {
      await submitPaymentRefundAdjustment(selectedPaymentLine.payment_log_id, data);
    }
    showSuccess("Payment Adjustment Added Successfully!");
    setShowAdjustmentModal(false);
    await refreshAfterAdminAction();
  };

  const resolveGateway = useCallback(
    (paymentSource: unknown, gatewayStatus: unknown) =>
      gatewayMetaLookup(
        gatewayMeta,
        paymentSource != null ? String(paymentSource) : undefined,
        gatewayStatus != null ? String(gatewayStatus) : undefined,
      ),
    [gatewayMeta],
  );

  return {
    applicationAllData,
    applicationData,
    applicantId,
    canClearSignature,
    canPerformPaymentActions,
    canEditPayment,
    canDeletePayment,
    canEditApplication,
    paymentFailedList,
    failedApiLogs,
    showAgreementsModal,
    clearingSignature,
    deletingPaymentId,
    showExternalPayment,
    externalPaymentType,
    showAdjustmentModal,
    selectedPaymentLine,
    nameOverrideChoice,
    nameOverrideReasonSelect,
    isSubmittingOverride,
    unfreezeReason,
    submittingUnfreeze,
    clearSignatureConfirmOpen,
    paymentDeleteConfirm,
    showAccessFeeBalanceDue,
    showBalanceOwed,
    balanceOwedAmount,
    parsedSignature,
    paymentLogRowsForCurrentApplicant,
    setShowAgreementsModal,
    setShowExternalPayment,
    setShowAdjustmentModal,
    setNameOverrideChoice,
    setNameOverrideReasonSelect,
    setUnfreezeReason,
    setClearSignatureConfirmOpen,
    setPaymentDeleteConfirm,
    performClearSignature,
    performDeletePayment,
    handleViewPaymentLog,
    handleEditPaymentLog,
    handleDownloadChargebackPacket,
    handleSaveNameOverride,
    handleUnfreezePayment,
    handlePaymentAdjustmentSubmit,
    refreshAfterAdminAction,
    resolveGateway,
  };
}

export type SignaturePaymentTabState = ReturnType<typeof useSignaturePaymentTab>;
